import { useIsMutating, useMutation, useMutationState } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { getMetaAuthFailureMessage } from '../../models/utils/metaAuthFailure';

const CONNECTION_MUTATION_KEY = ['socialMediaConnection'] as const;
const FINISH_CONNECTION_MUTATION_KEY = [...CONNECTION_MUTATION_KEY, 'finish'] as const;
const CONNECTION_MUTATION_SCOPE = { id: 'socialMediaConnection' } as const;

const platformPath = (type: Api.SocialMediaType) => type.toLowerCase();

const applyUpdatedUser = (userSession: Api.UserSessionContext, user: Api.IUser) => {
	if (user && userSession.user?.id === user.id) {
		userSession.updateUser(user);
	}
};

const disconnectConnection = (
	userSession: Api.UserSessionContext,
	type: Api.SocialMediaType,
	connection: Api.ISocialMediaConnection
) =>
	userSession.webServiceHelper.callAsync<Api.IUser>(
		Api.ImpersonationBroker.composeApiUrl({ urlPath: `user/socialMedia/${platformPath(type)}` }),
		'DELETE',
		connection
	);

const setConnectionPostTarget = (
	userSession: Api.UserSessionContext,
	{
		type,
		page,
		connection,
		accountId,
	}: {
		type: Api.SocialMediaType;
		page: Api.ISocialMediaPage;
		connection: Api.ISocialMediaConnection;
		accountId: string;
	}
) => {
	const request: Api.ISetPostTargetRequest = {
		connection,
		pageSelection: { pageId: page.id, pageName: page.name, userId: accountId },
	};
	return userSession.webServiceHelper.callAsync<Api.IUser>(
		Api.ImpersonationBroker.composeApiUrl({ urlPath: `user/socialMedia/${platformPath(type)}/postTargetPage` }),
		'PUT',
		request
	);
};

export const getSocialMediaConnectionErrorMessage = (error: unknown, fallback: string) => {
	const metaAuthFailureMessage = getMetaAuthFailureMessage(error);
	if (metaAuthFailureMessage) {
		return metaAuthFailureMessage;
	}
	const { systemCode, systemMessage } = (error ?? {}) as Api.IOperationResultNoValue;
	return systemCode === 400 && systemMessage ? systemMessage : fallback;
};

export function useSocialMediaAuthUrlMutation() {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: [...CONNECTION_MUTATION_KEY, 'authUrl'],
		mutationFn: ({ type }: { type: Api.SocialMediaType }) =>
			userSession.webServiceHelper.callAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `social/${platformPath(type)}/oauth/authorizeUrl` }),
				'POST'
			),
	});
}

export function useFinishSocialMediaConnectionMutation() {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: FINISH_CONNECTION_MUTATION_KEY,
		scope: CONNECTION_MUTATION_SCOPE,
		mutationFn: async ({ type, connection }: { type: Api.SocialMediaType; connection: Api.ISocialMediaConnection }) => {
			const { accountId, pages = [] } = await userSession.webServiceHelper.callAsync<Api.ISocialMediaPageCollection>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `social/${platformPath(type)}/account` }),
				'POST',
				connection
			);
			if (pages.length > 1) {
				return { accountId, pages, user: null as Api.IUser };
			}
			const user =
				pages.length === 1
					? await setConnectionPostTarget(userSession, { type, page: pages[0], connection, accountId })
					: await disconnectConnection(userSession, type, connection);
			return { accountId, pages, user };
		},
		onSuccess: ({ user }) => applyUpdatedUser(userSession, user),
	});
}

export function useSetSocialMediaPostTargetMutation() {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: FINISH_CONNECTION_MUTATION_KEY,
		scope: CONNECTION_MUTATION_SCOPE,
		mutationFn: (variables: {
			type: Api.SocialMediaType;
			page: Api.ISocialMediaPage;
			connection: Api.ISocialMediaConnection;
			accountId: string;
		}) => setConnectionPostTarget(userSession, variables),
		onSuccess: user => applyUpdatedUser(userSession, user),
	});
}

export function useDisconnectSocialMediaMutation() {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: [...CONNECTION_MUTATION_KEY, 'disconnect'],
		scope: CONNECTION_MUTATION_SCOPE,
		mutationFn: ({ type, connection }: { type: Api.SocialMediaType; connection: Api.ISocialMediaConnection }) =>
			disconnectConnection(userSession, type, connection),
		onSuccess: user => applyUpdatedUser(userSession, user),
	});
}

export const useIsSocialMediaConnectionBusy = () => useIsMutating({ mutationKey: CONNECTION_MUTATION_KEY }) > 0;

export const useFinishingSocialMediaConnectionType = () =>
	useMutationState({
		filters: { mutationKey: FINISH_CONNECTION_MUTATION_KEY, status: 'pending' },
		select: mutation => {
			const { variables } = mutation.state;
			return typeof variables === 'object' && variables !== null && 'type' in variables
				? Api.SupportedSocialMediaTypes.find(type => type === variables.type)
				: undefined;
		},
	})[0];
